import { useContext } from 'react';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';

import { products, fulfillmentStatuses, subscriptionActions } from '../../../constants';
import resolveFlexBillingFrequency, { isAnnually } from '../../../helpers/flex-helpers/flexBillingFrequency';
import { TrialDuration } from '../constants.ts';
import { PaymentContext } from '../../PaymentOrchestrator/PaymentContextProvider';

/**
 * Custom hook to calculate billing change information based on the current subscription and upgrade offer.
 *
 * @param {Object} params - The parameters for the hook.
 * @param {Object} params.upgradeOffer - The upgrade offer details.
 * @param {boolean} params.isTrial - Flag indicating if the current subscription is a trial.
 * @returns {Object} Billing change information.
 * @returns {Object} return.billingStartDate - The calculated start date for the new billing period.
 * @returns {Object} return.nextBillingDate - The end date of the current billing period.
 * @returns {boolean} return.isDeferredUpsell - Flag indicating if the upgrade offer is a deferred upsell.
 * @returns {string} return.periodicity - The billing frequency of the upgrade offer, capitalized.
 * @returns {number} return.price - The price of the upgrade offer.
 *
 * @example
 * const { billingStartDate, nextBillingDate, isDeferredUpsell, periodicity, price } = useBillingChangeInfo({
 *   upgradeOffer: {
 *     terms: [{ billingFrequency: 'monthly', price: 29.99 }],
 *     family: 'premium'
 *   },
 *   isTrial: true
 * });
 */

const DEFERRED_PRODUCT_MOVEMENTS = {
  [products.credit_expert]: [products.identity],
};

const BREACH_FAMILIES = [products.breach, products.breachPlus];

const useBillingChangeInfo = ({ upgradeOffer, isTrial }) => {
  const paymentContext = useContext(PaymentContext) || {};
  const { isFlexFlow = false, resolvedCurrentSubscription = null } = paymentContext;
  const reduxCurrentSubscription = useSelector(state => state.subscriptions.current);
  const currentSubscription = isFlexFlow ? resolvedCurrentSubscription : reduxCurrentSubscription;
  const proRataPrice = useSelector(state => state.proRataPrice.estimatedPrice);
  const changeBillingDatePaymentValue = useSelector(state => state.oneTimePayment?.price);
  // The switch page decides whether to call preview price before PaymentContext exists,
  // so the annual check has to come from the entitlements query cache.
  const entitlements = useSelector(
    state => state.entitlementsApi?.queries?.['getEntitlements(undefined)']?.data ?? null
  );

  const isMovingToCeFromId =
    currentSubscription?.family === products.identity && upgradeOffer?.family === products.credit_expert;

  const isBreachCustomer = BREACH_FAMILIES.includes(currentSubscription?.family);

  const isOnPaidProduct = [products.identity, products.credit_expert].includes(currentSubscription?.family);

  const isFulfillmentFailed = currentSubscription?.fulfillmentStatus === fulfillmentStatuses.failed;

  let isDeferredUpsell =
    DEFERRED_PRODUCT_MOVEMENTS[currentSubscription?.family]?.includes(upgradeOffer?.family) ?? false;

  const { nextBillingDate, billingEndDate } = useSelector(state => ({
    nextBillingDate: dayjs(state.billing.nextBillingDate),
    billingEndDate: dayjs(state.billing.endDate),
  }));

  const isPendingChange = [subscriptionActions.activation, subscriptionActions.cancellation].includes(
    currentSubscription?.defermentInfo?.type
  );

  const remainingTimeUntilDate = billingEndDate.isAfter(dayjs()) ? billingEndDate : null;

  const { billingFrequency, price } = upgradeOffer?.terms?.[0] ?? {};

  const periodicity = billingFrequency ? billingFrequency.charAt(0).toUpperCase() + billingFrequency.slice(1) : null;

  const trialPeriod = isTrial ? TrialDuration : 0;
  let billingStartDate;
  const invalidPaidSubscriptionState = isFulfillmentFailed && isOnPaidProduct;
  // The isDeferredUpsell flag is always set to false if invalidPaidSubscriptionState is true because
  // a failed fulfillment status on a paid product invalidates the possibility of a deferred upsell.
  if (invalidPaidSubscriptionState) {
    billingStartDate = dayjs().add(trialPeriod, 'day');
    isDeferredUpsell = false;
  } else if (isBreachCustomer) {
    billingStartDate = dayjs(remainingTimeUntilDate).add(trialPeriod, 'day');
  } else if (isDeferredUpsell) {
    billingStartDate = dayjs(nextBillingDate).add(1, 'day');
  } else {
    billingStartDate = dayjs().add(trialPeriod, 'day');
  }

  const isFlexUserOnFreeTrial = Boolean(currentSubscription?.planCode) && currentSubscription?.freeTrial;
  // An annual Identity customer moving onto a monthly CreditExpert plan has no pro-rata charge.
  // previewprice returns 0 for that movement, which the switch page treats as a failure and sends the customer home.
  const entitlementsTermFrequency = entitlements?.termFrequency ?? entitlements?.plans?.[0]?.termFrequency;
  const currentTerm =
    entitlementsTermFrequency ??
    currentSubscription?.termFrequency ??
    currentSubscription?.billingFrequency ??
    resolveFlexBillingFrequency({
      planCode: entitlements?.plans?.[0]?.planCode ?? currentSubscription?.planCode,
    });
  const isAnnualCustomer = isAnnually(currentTerm);
  const isMovingToMonthlyPlan = Boolean(billingFrequency) && !isAnnually(billingFrequency);
  // Phase 2: update free trial detection to use the entitlements response instead of subscription state
  const isProRataMovement =
    isMovingToCeFromId && !isFlexUserOnFreeTrial && !(isAnnualCustomer && isMovingToMonthlyPlan);

  return {
    billingStartDate,
    nextBillingDate,
    remainingTimeUntilDate,
    isDeferredUpsell,
    periodicity,
    billingFrequency,
    price,
    proRataPrice,
    isProRataMovement,
    isPendingChange,
    isOnPaidProduct,
    changeBillingDatePaymentValue,
  };
};

export default useBillingChangeInfo;
